import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Autocomplete,
  Grid,
} from "@mui/material";
import CollapsibleSection from "./CollapsibleSection";
import theme from "../../utils/theme";
import AppButton from "./AppButton";
import axios from "axios";

const styles = {
  container: {
    width: "100%",
    minWidth: 0,
    minHeight: 0,
    bgcolor: theme.palette.background.paper,
    borderRadius: 4,
    boxShadow: "0 2px 6px 0 rgba(44,62,80,0.14)",
    boxSizing: "border-box",
    overflow: "auto",
    padding: "1rem",
  },
  fieldRow: {
    display: "flex",
    alignItems: "center",
    mb: 2,
  },
  label: {
    minWidth: { xs: 120, lg: 150 },
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  valueBox: {
    bgcolor: theme.palette.background.default,
    minHeight: 40,
    display: "flex",
    alignItems: "center",
    p: 1,
    width: "100%",
    fontWeight: 700,
    color: theme.palette.text.primary,
    borderRadius: 1,
    boxSizing: "border-box",
  },
  autocomplete: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      width: "100%",
      height: 40,
      fontWeight: 700,
      bgcolor: theme.palette.background.paper,
    },
    "& input": {
      fontWeight: 700,
      width: "100%",
    },
  },
  workOrderRow: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    mb: 2,
  },
  workOrderTextField: {
    minWidth: 220,
    bgcolor: theme.palette.background.paper,
    minHeight: 40,
  },
};

const FieldBox = ({ label, value }) => (
  <Grid size={{ xs: 12, md: 6 }}>
    <Box sx={styles.fieldRow}>
      <Typography variant="h6" sx={styles.label}>
        {label}
      </Typography>
      <Typography variant="h6" sx={styles.valueBox}>
        {value}
      </Typography>
    </Box>
  </Grid>
);

const staticFields = (fields) => (
  <>
    <FieldBox label="Customer" value={fields.customer_name} />
    <FieldBox label="KD Number" value={fields.kd_number} />
    <FieldBox label="Project Title" value={fields.project_title} />
    <FieldBox
      label="Type of Asset"
      value={fields.type_of_asset || fields.asset_type}
    />
    {/* <FieldBox label="JIG Number" value={fields.jigNumber} /> */}
    {/* <FieldBox label="PO Number" value={fields.poNumber} /> */}
    <FieldBox
      label="Creation Date"
      value={new Date(fields.creation_date).toLocaleDateString()}
    />
    <FieldBox label="Build Workshop" value={fields.build_workshop} />
    <FieldBox
      label="Model"
      value={fields?.model_name || fields?.rfi_model?.model_name}
    />
  </>
);

export default function WorkOrderForm({
  fields,
  handleFieldChange,
  handleSubmit,
  editable = true,
  collapsibleOpen,
  setCollapsibleOpen,
  error = null,
}) {
  const [purpose, setPurpose] = useState([]);

  function convertArrayToObject(dataArray) {
    return dataArray?.reduce((acc, item) => {
      acc[item.purpose_id] = item.purpose_name;
      return acc;
    }, {});
  }

  const fetchPurpose = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/rfi-purpose`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("QC_accessToken")}`,
            Accept: "application/json",
          },
        }
      );
      const getPurposeObject = convertArrayToObject(response.data?.data || []);
      setPurpose(getPurposeObject);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (editable) {
      fetchPurpose();
    }
  }, [editable, fetchPurpose]);

  const purposeOptions = Object.entries(purpose).map(([key, label]) => ({
    key,
    label,
  }));

  if (!editable) {
    return (
      <CollapsibleSection
        header={
          "Work Order Number: " +
          (fields?.work_order_number || fields?.wo_number)
        }
        defaultOpen={collapsibleOpen}
        open={collapsibleOpen}
        onToggle={() =>
          setCollapsibleOpen && setCollapsibleOpen(!collapsibleOpen)
        }
        sx={{ bgcolor: theme.palette.background.paper }}
      >
        <Grid container spacing={1}>
          {staticFields(fields)}
          <FieldBox
            label="Purpose"
            value={fields.purpose?.purpose_name || fields?.purpose || ""}
          />
        </Grid>
      </CollapsibleSection>
    );
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.workOrderRow}>
        <Typography variant="h5">Work Order Number:</Typography>
        <TextField
          name="work_order_number"
          value={fields.work_order_number}
          onChange={handleFieldChange}
          size="small"
          sx={styles.workOrderTextField}
          required
          error={Boolean(error?.work_order_number)} // boolean
          helperText={error?.work_order_number}
        />
        <AppButton
          variantType="primary"
          onClick={handleSubmit}
          label="Submit"
          type="submit"
        />
      </Box>
      <Grid container spacing={1} sx={styles.fieldRow}>
        {staticFields(fields)}
        <Grid item size={{ xs: 12, md: 6 }}>
          <Box sx={styles.fieldRow}>
            <Typography sx={styles.label}>Purpose</Typography>
            <FormControl margin="dense" fullWidth>
              <Autocomplete
                options={purposeOptions}
                getOptionLabel={(option) => option.label}
                value={
                  purposeOptions.find((opt) => opt.key === fields.purpose) ||
                  null
                }
                onChange={(_, newValue) => {
                  handleFieldChange({
                    target: {
                      name: "purpose",
                      value: newValue?.key || "",
                    },
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Purpose"
                    required
                    error={Boolean(error?.purpose)}
                    helperText={error?.purpose}
                  />
                )}
                sx={styles.autocomplete}
                disableClearable
                isOptionEqualToValue={(option, value) =>
                  option.key === value.key
                }
              />
            </FormControl>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
